import type { LabelReconciliation, RecordedLabelReconciliation } from '@/api/productLabelsApi'
import { useLabelReconciliation } from '@/hooks/useProductLabels'
import { reconciliationLine, signedText } from '@/lib/productLabels'
import { cn } from '@/lib/utils'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ProductLabelsTable } from './ProductLabelsTable'

/**
 * The labels printed for one form's products (z8nrz7dt48), with the form's
 * reconciliation on top — where the operator verifies, discards or returns
 * them before completing the form.
 */
export function RunLabelsDialog({
  submissionId,
  open,
  onClose,
}: {
  submissionId: string | null
  open: boolean
  onClose: () => void
}) {
  const summary = useLabelReconciliation(open ? submissionId : null)
  return (
    <Dialog open={open && !!submissionId} onOpenChange={o => !o && onClose()}>
      <DialogContent className='max-h-[90vh] overflow-y-auto sm:max-w-6xl'>
        <DialogHeader>
          <DialogTitle>Labels on this form</DialogTitle>
          <DialogDescription>
            Every label printed for this form&apos;s products. Verify each one after printing, or discard or return
            it — the form completes once every label is accounted for.
          </DialogDescription>
        </DialogHeader>
        {summary.data?.recorded && <RecordedReconciliationBanner recorded={summary.data.recorded} />}
        {summary.data && <ReconciliationBanner summary={summary.data} />}
        {submissionId && <ProductLabelsTable scope={{ submissionId }} />}
      </DialogContent>
    </Dialog>
  )
}

/** issued = applied + discarded + unused, green when it balances. */
export function ReconciliationBanner({ summary }: { summary: LabelReconciliation }) {
  if (summary.issued === 0) return null
  return (
    <div
      className={cn(
        'rounded-md border px-3 py-2 text-sm',
        summary.balanced ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-900',
      )}
    >
      <span className='font-medium'>{summary.balanced ? 'Labels reconcile: ' : 'Labels do not reconcile yet: '}</span>
      {reconciliationLine(summary)}
      {summary.pendingLabels.length > 0 && (
        <span> — waiting: {summary.pendingLabels.map(l => `#${l.number}`).join(', ')}</span>
      )}
    </div>
  )
}

/**
 * The reconciliation the form was completed with (AC7) — part of its record,
 * so it is the signed counts, not today's: a label discarded later does not
 * rewrite what was signed.
 */
export function RecordedReconciliationBanner({
  recorded,
  onOpenLabels,
}: {
  recorded: RecordedLabelReconciliation
  onOpenLabels?: () => void
}) {
  return (
    <div
      className={cn(
        'rounded-md border px-3 py-2 text-sm',
        recorded.balanced ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-900',
      )}
      data-testid='recorded-label-reconciliation'
    >
      <div className='flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1'>
        <span>
          <span className='font-medium'>
            {recorded.balanced ? 'Labels reconciled at completion: ' : 'Completed with a label discrepancy: '}
          </span>
          {reconciliationLine(recorded)}
          {recorded.unaccounted.length > 0 && (
            <span> — unaccounted: {recorded.unaccounted.map(n => `#${n}`).join(', ')}</span>
          )}
        </span>
        <span className='text-xs opacity-80'>
          Signed {signedText(recorded.signedBy, recorded.signedAt)}
          {onOpenLabels && (
            <>
              {' · '}
              <button type='button' className='underline underline-offset-2' onClick={onOpenLabels}>
                View labels
              </button>
            </>
          )}
        </span>
      </div>
      {recorded.discrepancy && (
        <p className='mt-1'>
          <span className='font-medium'>Reason: </span>
          {recorded.discrepancy}
        </p>
      )}
    </div>
  )
}
